import { SyntheticEvent, useMemo, useState } from 'react'

import { useInfiniteQuery, useQuery } from '@api/hooks'
import { Manufacturer } from '@domain'
import useDebounce from '@hooks/useDebounce'
import { AutocompleteProps, ListItem, ListItemText, Stack, SxProps, Theme } from '@mui/material'
import ManufacturersApi, { ManufacturersKey } from '@services/manufacturers.api'
import { AxiosError } from 'axios'
import Highlight from 'react-highlighter'
import { FieldValues, Path } from 'react-hook-form'

import RHFAutocomplete from './RHFAutocomplete'

type Props<T extends FieldValues> = Omit<
	AutocompleteProps<Manufacturer, false, false, false>,
	'renderInput' | 'options'
> & {
	name: Path<T>
	label: string
	required?: boolean
	fullWidth?: boolean
	sx?: SxProps<Theme>
}

export default function ManufacturerAutocomplete<T extends FieldValues>({
	required = false,
	...props
}: Props<T>) {
	const [searchValue, setSearchValue] = useState('')
	const [focused, setFocused] = useState(false)

	const debouncedSearchValue: string = useDebounce(searchValue, 200)

	const { data, isLoading, error } = useInfiniteQuery({
		queryKey: ManufacturersKey.list({ input: debouncedSearchValue }),
		queryFn: ({ pageParam = 1 }) =>
			ManufacturersApi.list({
				input: debouncedSearchValue,
				page: pageParam,
			}),
		getNextPageParam: (lastPage) => {
			const { page, totalPages } = lastPage.pagination
			return page < totalPages ? page + 1 : undefined
		},
		enabled: debouncedSearchValue.length > 1 && focused,
		throwOnError: false,
		initialPageParam: 1,
	})

	const options = useMemo(() => data?.pages.flatMap((page) => page.data || []) ?? [], [data])

	const handleInputChange = (event: SyntheticEvent, value: string) => {
		setSearchValue(value)
	}

	const errorMessage = error ? (error as AxiosError<ApiErrorRes>).response?.data?.message : null

	const getNoOptionsText = () => {
		if (errorMessage) return errorMessage
		return data ? 'No Results Found' : 'Type at least 2 characters to begin search'
	}

	return (
		<RHFAutocomplete
			{...props}
			required={required}
			// filterOptions func needed for search on type functionality to work properly
			filterOptions={(x) => x}
			noOptionsText={getNoOptionsText()}
			inputValue={searchValue}
			onInputChange={handleInputChange}
			options={options}
			loading={isLoading}
			onFocus={() => setFocused(true)}
			onBlur={() => setFocused(false)}
			getOptionLabel={(option: CategorySearchResult) => option.name}
			renderOption={(props, option: CategorySearchResult) => (
				<ListItem {...props} key={option.id} disablePadding>
					<ListItemText
						primary={<Highlight search={searchValue}>{option.name}</Highlight>}
						secondary={
							<Stack width="100%" direction="row" justifyContent="space-between" gap={1}>
								<Highlight search={searchValue}>{option.parentName || ''}</Highlight>
								<Highlight search={searchValue}>{`PLID: ${option.plid || ''}`}</Highlight>
							</Stack>
						}
					/>
				</ListItem>
			)}
			getOptionKey={(option: CategorySearchResult) => option.id}
		/>
	)
}
